import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { useTheme } from 'next-themes';
import { useRef, useState } from 'react';
import { MapPin, Flag, Loader2, X, Crosshair } from 'lucide-react';
import { Input } from '../ui/input';
import { GOOGLE_MAPS_LIBRARIES, useGoogleMapsRuntimeError } from '../../lib/google-maps';

const darkStyles = [
  { elementType: 'geometry', stylers: [{ color: '#0a0a0a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0a0a0a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#737373' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1a1a1a' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#050505' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const LAGOS = { lat: 6.5244, lng: 3.3792 };

/**
 * MapPointPicker — drop Start / Finish pins and (optionally) numbered
 * checkpoints. Three modes:
 *   • start      — tap map drops the start pin
 *   • finish     — tap map drops the finish pin
 *   • checkpoint — tap map APPENDS a checkpoint
 *
 * All pins (Start, Finish, every checkpoint) are draggable to fine-tune.
 *
 * Props:
 *   start, finish — { lat, lng } | null
 *   checkpoints   — Array<{ name, lat, lng }>
 *   onChange      — ({ start, finish, checkpoints }) => void  (all three returned every time)
 */
export function MapPointPicker({ start, finish, checkpoints = [], onChange, height = 280 }) {
  const { theme } = useTheme();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey || '', libraries: GOOGLE_MAPS_LIBRARIES });
  const gmErrored = useGoogleMapsRuntimeError(isLoaded);

  const [mode, setMode] = useState('start');
  const [search, setSearch] = useState('');
  const [center, setCenter] = useState(start || finish || LAGOS);
  const mapRef = useRef(null);
  const acRef = useRef(null);

  // Note: intentionally NOT re-centering the map when start/finish props change.
  // Doing so would snap the camera back to the start every time the user dropped
  // a finish pin, which felt like the finish was "reverting". Pan only happens
  // explicitly via the search Autocomplete.

  if (!apiKey) {
    return (
      <div className="rivl-card p-4 text-center text-xs text-muted-foreground" data-testid="picker-no-key">
        Map picker needs a Google Maps API key in <span className="font-mono">.env</span>.
      </div>
    );
  }
  if (loadError || gmErrored) {
    return (
      <div className="rivl-card p-4 text-center text-xs text-muted-foreground space-y-2" data-testid="picker-load-error">
        <p className="font-semibold text-foreground">Map unavailable</p>
        <p>Google Maps could not load. Verify your Google Cloud project has billing enabled and the API key allows this domain, then reload.</p>
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className="rivl-card p-6 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Always pass the full triple back to the parent so callers don't need to
  // worry about which mode was active.
  function emit({ start: s = start, finish: f = finish, checkpoints: c = checkpoints }) {
    onChange({ start: s, finish: f, checkpoints: c });
  }

  function setPoint(point) {
    if (mode === 'start') emit({ start: point });
    else if (mode === 'finish') emit({ finish: point });
    else if (mode === 'checkpoint') {
      const next = [...checkpoints, { name: `Checkpoint ${checkpoints.length + 1}`, lat: point.lat, lng: point.lng }];
      emit({ checkpoints: next });
    }
  }

  function handleMapClick(e) {
    setPoint({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }

  function handleStartDrag(e) { emit({ start: { lat: e.latLng.lat(), lng: e.latLng.lng() } }); }
  function handleFinishDrag(e) { emit({ finish: { lat: e.latLng.lat(), lng: e.latLng.lng() } }); }
  function handleCheckpointDrag(i, e) {
    const next = checkpoints.slice();
    next[i] = { ...next[i], lat: e.latLng.lat(), lng: e.latLng.lng() };
    emit({ checkpoints: next });
  }
  function removeCheckpoint(i) {
    const next = checkpoints.slice();
    next.splice(i, 1);
    emit({ checkpoints: next });
  }

  function clear(which) {
    if (which === 'start') emit({ start: null });
    else if (which === 'finish') emit({ finish: null });
  }

  function switchMode(next) { setMode(next); setSearch(''); }

  function onPlaceChanged() {
    const place = acRef.current?.getPlace();
    const loc = place?.geometry?.location;
    if (!loc) return;
    const point = { lat: loc.lat(), lng: loc.lng() };
    setCenter(point);
    if (mapRef.current) mapRef.current.panTo(point);
    setPoint(point);
    setSearch('');
  }

  const placeholder =
    mode === 'start' ? 'Search address for START point...'
    : mode === 'finish' ? 'Search address for FINISH point...'
    : 'Search address to add a CHECKPOINT...';

  const tapHint =
    mode === 'start' ? 'Tap map to drop START. Drag pins to fine-tune.'
    : mode === 'finish' ? 'Tap map to drop FINISH. Drag pins to fine-tune.'
    : 'Tap map to add a CHECKPOINT. Drag any pin to fine-tune.';

  return (
    <div className="space-y-2" data-testid="map-point-picker">
      <div className="grid grid-cols-3 gap-2">
        <ModeButton active={mode === 'start'} onClick={() => switchMode('start')} icon={MapPin} label="Start" point={start} tone="success" testid="picker-mode-start" />
        <ModeButton active={mode === 'finish'} onClick={() => switchMode('finish')} icon={Flag} label="Finish" point={finish} tone="primary" testid="picker-mode-finish" />
        <ModeButton active={mode === 'checkpoint'} onClick={() => switchMode('checkpoint')} icon={Crosshair} label={checkpoints.length > 0 ? `CP (${checkpoints.length})` : 'Checkpoint'} point={checkpoints.length > 0 ? true : null} tone="blue" testid="picker-mode-checkpoint" />
      </div>

      <Autocomplete
        onLoad={(ac) => (acRef.current = ac)}
        onPlaceChanged={onPlaceChanged}
        options={{ componentRestrictions: { country: 'ng' } }}
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
          className="h-10 rounded-md text-sm"
          data-testid="picker-search"
        />
      </Autocomplete>

      <div className="rounded-md overflow-hidden border border-border" style={{ height }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center}
          zoom={start || finish ? 15 : 12}
          onLoad={(map) => (mapRef.current = map)}
          onClick={handleMapClick}
          options={{
            styles: theme === 'dark' ? darkStyles : [],
            disableDefaultUI: true,
            zoomControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            rotateControl: false,
            scaleControl: false,
            panControl: false,
            keyboardShortcuts: false,
            clickableIcons: false,
            gestureHandling: 'greedy',
          }}
        >
          {start && (
            <Marker
              position={start}
              draggable
              onDragEnd={handleStartDrag}
              label={{ text: 'S', color: '#fff', fontWeight: 'bold' }}
              icon={{
                path: 'M 0,0 m -12,0 a 12,12 0 1,0 24,0 a 12,12 0 1,0 -24,0',
                fillColor: '#16a34a', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 1,
                labelOrigin: { x: 0, y: 0 },
              }}
            />
          )}
          {finish && (
            <Marker
              position={finish}
              draggable
              onDragEnd={handleFinishDrag}
              label={{ text: 'F', color: '#fff', fontWeight: 'bold' }}
              icon={{
                path: 'M 0,0 m -12,0 a 12,12 0 1,0 24,0 a 12,12 0 1,0 -24,0',
                fillColor: '#E63946', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 1,
                labelOrigin: { x: 0, y: 0 },
              }}
            />
          )}
          {checkpoints.map((cp, i) => (
            cp.lat && cp.lng ? (
              <Marker
                key={`cp-${i}`}
                position={{ lat: cp.lat, lng: cp.lng }}
                draggable
                onDragEnd={(e) => handleCheckpointDrag(i, e)}
                onClick={() => removeCheckpoint(i)}
                label={{ text: String(i + 1), color: '#fff', fontWeight: 'bold' }}
                icon={{
                  path: 'M 0,0 m -10,0 a 10,10 0 1,0 20,0 a 10,10 0 1,0 -20,0',
                  fillColor: '#1D4ED8', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 1,
                  labelOrigin: { x: 0, y: 0 },
                }}
                title={`${cp.name || `Checkpoint ${i + 1}`} (drag to move, tap to remove)`}
              />
            ) : null
          ))}
        </GoogleMap>
      </div>

      <p className="text-[10px] text-muted-foreground">{tapHint}</p>

      <div className="grid grid-cols-2 gap-2">
        <PointReadout point={start} which="start" onClear={clear} testid="picker-readout-start" />
        <PointReadout point={finish} which="finish" onClear={clear} testid="picker-readout-finish" />
      </div>
    </div>
  );
}

function ModeButton({ active, onClick, icon: Icon, label, point, tone, testid }) {
  const ring =
    tone === 'success' ? 'border-success bg-success/5'
    : tone === 'blue' ? 'border-brand-blue bg-brand-blue/5'
    : 'border-primary bg-primary/5';
  const iconColor =
    tone === 'success' ? 'text-success'
    : tone === 'blue' ? 'text-brand-blue'
    : 'text-primary';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center justify-center gap-1.5 h-10 rounded-md border-2 text-xs font-semibold uppercase tracking-widest transition-colors ${active ? ring : 'border-border'}`}
    >
      <Icon className={`h-3.5 w-3.5 ${iconColor}`} />
      {label}
      {point && <span className="ml-0.5 text-[9px] font-mono opacity-60">·set</span>}
    </button>
  );
}

function PointReadout({ point, which, onClear, testid }) {
  if (!point) {
    return (
      <div className="rivl-card px-2.5 py-1.5 text-muted-foreground" data-testid={testid}>
        <span className="rivl-label text-[8px]">{which}</span>
        <div className="font-mono text-[10px] mt-0.5">— not set —</div>
      </div>
    );
  }
  return (
    <div className="rivl-card px-2.5 py-1.5 flex items-center justify-between" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <span className="rivl-label text-[8px]">{which}</span>
        <div className="font-mono text-[10px] truncate">{point.lat.toFixed(4)}, {point.lng.toFixed(4)}</div>
      </div>
      <button type="button" onClick={() => onClear(which)} className="h-5 w-5 rounded-md hover:bg-secondary flex items-center justify-center" data-testid={`${testid}-clear`}>
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
